/**
 * LeakAreaPhotoStrip — Iter164 Phase A.
 *
 * Compact thumbnail strip rendered at the bottom of each leak-area
 * card inside the Remedial Repairs builder. Shows up to 4 attached
 * photos with a "+N more" overflow; clicking opens a lightbox.
 * "Attach photo" button opens AttachPhotoModal which lets the
 * operator either:
 *   - Attach from the appraisal's existing gallery (Defect / Other /
 *     Before / etc. — anything unlinked), OR
 *   - Upload a new photo that auto-links to this leak area
 *
 * Photo→leak_area is a 1-to-many relation stored on photo.leak_area_id
 * (one-to-many per user spec 2b + storage spec 3c).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Camera, Plus, X, Eye, EyeOff, Trash2, Upload, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LeakAreaPhotoStrip({ api, appraisalId, area }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/photos/${appraisalId}?leak_area_id=${encodeURIComponent(area.area_id)}`);
      setPhotos(data || []);
    } catch (e) {
      console.error('Leak photo load failed', e);
    } finally {
      setLoading(false);
    }
  }, [api, appraisalId, area.area_id]);

  useEffect(() => { load(); }, [load]);

  const detach = async (photoId) => {
    if (!window.confirm('Unlink this photo from the leak area? (The photo stays in the gallery.)')) return;
    try {
      await api.patch(`/photos/${photoId}`, { leak_area_id: null });
      await load();
      toast.success('Photo unlinked');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to unlink photo');
    }
  };

  const toggleClientFacing = async (photo) => {
    try {
      await api.patch(`/photos/${photo._id}`, { is_client_facing: !photo.is_client_facing });
      await load();
      if (lightboxPhoto?._id === photo._id) {
        setLightboxPhoto(p => ({ ...p, is_client_facing: !p.is_client_facing }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Toggle failed');
    }
  };

  const visible = photos.slice(0, 4);
  const overflow = Math.max(0, photos.length - visible.length);

  return (
    <div className="flex items-start gap-2 pt-2 border-t border-rose-100" data-testid={`rr-area-photos-${area.area_id}`}>
      <span className="text-[11px] font-bold uppercase tracking-wider text-rose-700 flex items-center gap-1 mt-1">
        <Camera size={11} /> Photos
      </span>
      <div className="flex-1 flex items-center gap-1.5 flex-wrap">
        {loading && photos.length === 0 ? (
          <span className="text-[10px] text-slate-400 italic">loading…</span>
        ) : photos.length === 0 ? (
          <span className="text-[10px] text-slate-400 italic">No photos attached yet</span>
        ) : (
          <>
            {visible.map(p => (
              <div key={p._id} className="relative group" data-testid={`rr-area-photo-thumb-${p._id}`}>
                <img
                  src={`${BACKEND_URL}${p.url}`}
                  alt={p.description || p.original_name}
                  onClick={() => setLightboxPhoto(p)}
                  className="w-14 h-14 object-cover rounded border border-slate-200 cursor-pointer hover:ring-2 hover:ring-rose-400 transition-shadow"
                />
                {/* Client/internal tint dot */}
                <span
                  className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${
                    p.is_client_facing ? 'bg-emerald-500' : 'bg-slate-400'
                  }`}
                  title={p.is_client_facing ? 'Customer-facing' : 'Internal only'}
                />
              </div>
            ))}
            {overflow > 0 && (
              <button
                type="button"
                onClick={() => setLightboxPhoto(photos[4])}
                className="w-14 h-14 rounded border border-dashed border-slate-300 bg-slate-50 text-xs font-bold text-slate-500 hover:bg-slate-100"
              >
                +{overflow}
              </button>
            )}
          </>
        )}
        <button
          type="button"
          data-testid={`rr-area-attach-photo-${area.area_id}`}
          onClick={() => setShowAttach(true)}
          className="w-14 h-14 rounded border border-dashed border-rose-300 bg-rose-50/40 text-rose-700 hover:bg-rose-50 flex flex-col items-center justify-center gap-0.5"
          title="Attach photo(s)"
        >
          <Plus size={14} />
          <span className="text-[9px] font-bold uppercase tracking-wide">Attach</span>
        </button>
      </div>

      {showAttach && (
        <AttachPhotoModal
          api={api}
          appraisalId={appraisalId}
          area={area}
          onClose={() => setShowAttach(false)}
          onSaved={() => { setShowAttach(false); load(); }}
        />
      )}

      {lightboxPhoto && (
        <PhotoLightbox
          photo={lightboxPhoto}
          allPhotos={photos}
          onClose={() => setLightboxPhoto(null)}
          onJump={setLightboxPhoto}
          onDetach={detach}
          onToggleClientFacing={toggleClientFacing}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
function AttachPhotoModal({ api, appraisalId, area, onClose, onSaved }) {
  const [tab, setTab] = useState('gallery'); // 'gallery' | 'upload' | 'library'
  // Gallery tab state
  const [unlinked, setUnlinked] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState('Defect'); // most common case
  // Upload tab state
  const [uploadCat, setUploadCat] = useState('Defect');
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploading, setUploading] = useState(false);
  // Examples library tab state (Iter165 Phase C)
  const [library, setLibrary] = useState([]);
  const [libSearch, setLibSearch] = useState('');
  const [libSelected, setLibSelected] = useState(new Set());
  const [libCloning, setLibCloning] = useState(false);

  const loadUnlinked = useCallback(async () => {
    try {
      // Pull every photo on this appraisal and filter client-side —
      // we want photos NOT linked to ANY leak area, OR linked to a
      // different one (so operator can re-link). Per spec the
      // simpler 1-to-many model means we just exclude photos already
      // linked to THIS area.
      const { data } = await api.get(`/photos/${appraisalId}`);
      const list = (data || []).filter(p => p.leak_area_id !== area.area_id);
      setUnlinked(list);
    } catch (e) {
      console.error(e);
    }
  }, [api, appraisalId, area.area_id]);

  useEffect(() => { loadUnlinked(); }, [loadUnlinked]);

  // Iter165 Phase C — fetch cross-appraisal Example photos when the
  // operator switches to the Library tab. Excludes Examples that
  // were originally uploaded against THIS appraisal (no point cloning
  // an existing local photo).
  const loadLibrary = useCallback(async () => {
    try {
      const { data } = await api.get(`/photos/examples-library?q=${encodeURIComponent(libSearch)}`);
      const list = (data || []).filter(p => p.source_appraisal_id !== appraisalId);
      setLibrary(list);
    } catch (e) {
      console.error(e);
    }
  }, [api, libSearch, appraisalId]);

  useEffect(() => { if (tab === 'library') loadLibrary(); }, [tab, loadLibrary]);

  const filtered = unlinked.filter(p => filter === 'All' ? true : (p.category || 'Other') === filter);

  const toggle = (id) => {
    setSelected(s => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const attachSelected = async () => {
    if (selected.size === 0) {
      toast.error('Pick at least one photo');
      return;
    }
    try {
      await Promise.all(Array.from(selected).map(id =>
        api.patch(`/photos/${id}`, { leak_area_id: area.area_id })
      ));
      toast.success(`Linked ${selected.size} photo${selected.size === 1 ? '' : 's'}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to attach photos');
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('appraisal_id', appraisalId);
        formData.append('description', uploadDesc || '');
        formData.append('category', uploadCat);
        formData.append('leak_area_id', area.area_id);
        await api.post('/photos/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success(`Uploaded ${files.length} photo${files.length === 1 ? '' : 's'}`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Iter165 Phase C — clone every selected library photo onto THIS
  // appraisal, auto-linked to this leak area. Each clone duplicates
  // the file on disk (server-side) so the new photo doc owns its own
  // copy and can be edited/deleted independently of the source.
  const toggleLib = (id) => {
    setLibSelected(s => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const cloneSelected = async () => {
    if (libSelected.size === 0) {
      toast.error('Pick at least one example');
      return;
    }
    setLibCloning(true);
    try {
      await Promise.all(Array.from(libSelected).map(id =>
        api.post('/photos/examples-library/clone', {
          source_photo_id: id,
          target_appraisal_id: appraisalId,
          leak_area_id: area.area_id,
        })
      ));
      toast.success(`Cloned ${libSelected.size} example${libSelected.size === 1 ? '' : 's'}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clone');
    } finally {
      setLibCloning(false);
    }
  };

  const CATEGORIES = ['All', 'Defect', 'Before', 'During', 'After', 'Example', 'Other'];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="rr-attach-photo-modal"
      >
        {/* Header */}
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-slate-900 font-[Manrope]">Attach photos to {area.label || 'leak area'}</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Link existing photos from the gallery — or upload new ones — to this leak area.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 flex gap-1 px-5">
          <button
            data-testid="rr-attach-tab-gallery"
            onClick={() => setTab('gallery')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
              tab === 'gallery' ? 'border-rose-600 text-rose-700' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <ImageIcon size={14} className="inline mr-1" /> Attach from gallery ({unlinked.length})
          </button>
          <button
            data-testid="rr-attach-tab-upload"
            onClick={() => setTab('upload')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
              tab === 'upload' ? 'border-rose-600 text-rose-700' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Upload size={14} className="inline mr-1" /> Upload new
          </button>
          <button
            data-testid="rr-attach-tab-library"
            onClick={() => setTab('library')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
              tab === 'library' ? 'border-rose-600 text-rose-700' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <ImageIcon size={14} className="inline mr-1" /> Examples Library
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-5">
          {tab === 'gallery' ? (
            <>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Filter category:</span>
                {CATEGORIES.map(c => {
                  const count = c === 'All' ? unlinked.length : unlinked.filter(p => (p.category || 'Other') === c).length;
                  return (
                    <button
                      key={c}
                      onClick={() => setFilter(c)}
                      className={`px-2 py-1 text-[11px] font-semibold rounded ${
                        filter === c ? 'bg-rose-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {c} <span className="opacity-70">({count})</span>
                    </button>
                  );
                })}
              </div>
              {filtered.length === 0 ? (
                <div className="text-center py-10 text-sm text-slate-400">
                  No unattached photos in this category. Upload a new one via the <strong>Upload</strong> tab.
                </div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                  {filtered.map(p => (
                    <button
                      key={p._id}
                      type="button"
                      data-testid={`rr-attach-pick-${p._id}`}
                      onClick={() => toggle(p._id)}
                      className={`relative aspect-square rounded border-2 overflow-hidden ${
                        selected.has(p._id) ? 'border-rose-600 ring-2 ring-rose-300' : 'border-slate-200 hover:border-rose-300'
                      }`}
                    >
                      <img
                        src={`${BACKEND_URL}${p.url}`}
                        alt={p.description || p.original_name}
                        className="w-full h-full object-cover"
                      />
                      <span className="absolute top-1 left-1 px-1.5 py-0.5 text-[9px] font-bold rounded bg-white/90 text-slate-700">
                        {p.category || 'Other'}
                      </span>
                      {selected.has(p._id) && (
                        <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-rose-600 text-white flex items-center justify-center text-xs font-bold">✓</span>
                      )}
                      {p.leak_area_id && p.leak_area_id !== area.area_id && (
                        <span className="absolute bottom-1 right-1 px-1 py-0.5 text-[8px] font-bold rounded bg-amber-100 text-amber-900" title="Linked to another leak area — selecting will re-link to this one">
                          ↺ relink
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : tab === 'upload' ? (
            <div className="space-y-3 max-w-xl">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Category</label>
                <select
                  data-testid="rr-upload-category"
                  value={uploadCat}
                  onChange={e => setUploadCat(e.target.value)}
                  className="w-full mt-1 text-sm border border-slate-300 rounded px-2 py-1.5 bg-white"
                >
                  {['Defect', 'Before', 'During', 'After', 'Example', 'Other'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Description (optional)</label>
                <input
                  data-testid="rr-upload-description"
                  type="text"
                  value={uploadDesc}
                  onChange={e => setUploadDesc(e.target.value)}
                  placeholder="e.g. Crack in ridge tile at NE corner"
                  className="w-full mt-1 text-sm border border-slate-300 rounded px-2 py-1.5"
                />
              </div>
              <label
                data-testid="rr-upload-dropzone"
                className="block border-2 border-dashed border-rose-300 rounded p-8 text-center bg-rose-50/30 cursor-pointer hover:bg-rose-50"
              >
                <Upload size={28} className="text-rose-400 mx-auto mb-2" />
                <p className="text-sm font-semibold text-rose-800">Click to select photos</p>
                <p className="text-[11px] text-slate-500 mt-1">JPG / PNG / HEIC — multiple allowed</p>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleUpload}
                  disabled={uploading}
                  className="sr-only"
                  data-testid="rr-upload-input"
                />
              </label>
              {uploading && <div className="text-center text-sm text-slate-500">Uploading…</div>}
            </div>
          ) : (
            /* Iter165 Phase C — Examples Library tab. Lists every
               photo with category=Example across ALL appraisals
               (excluding this one). Each card shows the source
               property address so the operator recognises the
               context. Selecting + Clone duplicates the file onto
               this appraisal + auto-links to this leak area. */
            <div data-testid="rr-attach-library-body">
              <div className="flex items-center gap-2 mb-3">
                <input
                  data-testid="rr-library-search"
                  type="text"
                  value={libSearch}
                  onChange={e => setLibSearch(e.target.value)}
                  placeholder="Search by description or source property…"
                  className="flex-1 text-sm border border-slate-300 rounded px-3 py-1.5"
                />
                <button
                  type="button"
                  onClick={loadLibrary}
                  className="px-3 py-1.5 text-sm font-semibold rounded bg-slate-100 text-slate-700 hover:bg-slate-200"
                >
                  Refresh
                </button>
              </div>
              {library.length === 0 ? (
                <div className="text-center py-10 text-sm text-slate-400">
                  No reference Examples in the library yet. Tag any inspection photo as <strong>Example</strong> in the Photo Gallery to seed the library.
                </div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                  {library.map(p => (
                    <button
                      key={p._id}
                      type="button"
                      data-testid={`rr-library-pick-${p._id}`}
                      onClick={() => toggleLib(p._id)}
                      className={`relative aspect-square rounded border-2 overflow-hidden text-left ${
                        libSelected.has(p._id) ? 'border-rose-600 ring-2 ring-rose-300' : 'border-slate-200 hover:border-rose-300'
                      }`}
                    >
                      <img
                        src={`${BACKEND_URL}${p.url}`}
                        alt={p.description || p.original_name}
                        className="w-full h-full object-cover"
                      />
                      <span className="absolute top-1 left-1 px-1.5 py-0.5 text-[9px] font-bold rounded bg-violet-100 text-violet-900">
                        Example
                      </span>
                      {libSelected.has(p._id) && (
                        <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-rose-600 text-white flex items-center justify-center text-xs font-bold">✓</span>
                      )}
                      <span className="absolute bottom-0 left-0 right-0 px-1.5 py-1 text-[9px] font-semibold bg-black/55 text-white truncate" title={p.source_property_address}>
                        {p.source_property_address || '—'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {tab === 'gallery' && (
          <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-3">
            <span className="text-xs text-slate-500">{selected.size} selected</span>
            <div className="flex items-center gap-2">
              <button onClick={onClose} className="text-sm px-3 py-1.5 text-slate-600 hover:text-slate-900">Cancel</button>
              <button
                data-testid="rr-attach-confirm"
                onClick={attachSelected}
                disabled={selected.size === 0}
                className="bg-rose-600 text-white text-sm font-semibold rounded-md px-4 py-1.5 hover:bg-rose-700 disabled:opacity-40"
              >
                Attach {selected.size} photo{selected.size === 1 ? '' : 's'}
              </button>
            </div>
          </div>
        )}
        {tab === 'library' && (
          <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-3">
            <span className="text-xs text-slate-500">{libSelected.size} selected · cloned copies will be linked to <strong>{area.label || 'this area'}</strong></span>
            <div className="flex items-center gap-2">
              <button onClick={onClose} className="text-sm px-3 py-1.5 text-slate-600 hover:text-slate-900">Cancel</button>
              <button
                data-testid="rr-library-clone-confirm"
                onClick={cloneSelected}
                disabled={libSelected.size === 0 || libCloning}
                className="bg-rose-600 text-white text-sm font-semibold rounded-md px-4 py-1.5 hover:bg-rose-700 disabled:opacity-40"
              >
                {libCloning ? 'Cloning…' : `Clone ${libSelected.size} example${libSelected.size === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
function PhotoLightbox({ photo, allPhotos, onClose, onJump, onDetach, onToggleClientFacing }) {
  const idx = allPhotos.findIndex(p => p._id === photo._id);
  const next = () => onJump(allPhotos[(idx + 1) % allPhotos.length]);
  const prev = () => onJump(allPhotos[(idx - 1 + allPhotos.length) % allPhotos.length]);
  return (
    <div
      className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="rr-photo-lightbox"
    >
      <div className="relative max-w-5xl w-full" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-10 right-0 text-white hover:text-slate-300"><X size={22} /></button>
        <img src={`${BACKEND_URL}${photo.url}`} alt={photo.description || ''} className="w-full max-h-[70vh] object-contain rounded" />
        {allPhotos.length > 1 && (
          <>
            <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 text-white hover:bg-black/70">‹</button>
            <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 text-white hover:bg-black/70">›</button>
          </>
        )}
        <div className="mt-3 flex items-center gap-2 flex-wrap text-white">
          <span className="px-2 py-1 rounded bg-white/10 text-xs">{photo.category || 'Other'}</span>
          <span className="text-sm flex-1 truncate">{photo.description || photo.original_name}</span>
          <button
            type="button"
            data-testid={`rr-lightbox-toggle-cf-${photo._id}`}
            onClick={() => onToggleClientFacing(photo)}
            className={`px-2 py-1 rounded text-xs flex items-center gap-1 border ${
              photo.is_client_facing
                ? 'bg-emerald-500/30 text-emerald-100 border-emerald-400/50'
                : 'bg-white/10 text-slate-300 border-white/20'
            }`}
          >
            {photo.is_client_facing ? <Eye size={12} /> : <EyeOff size={12} />}
            {photo.is_client_facing ? 'Customer' : 'Internal'}
          </button>
          <button
            type="button"
            data-testid={`rr-lightbox-detach-${photo._id}`}
            onClick={() => { onDetach(photo._id); onClose(); }}
            className="px-2 py-1 rounded text-xs flex items-center gap-1 bg-rose-500/30 text-rose-100 border border-rose-400/50 hover:bg-rose-500/50"
            title="Unlink from this leak area (photo stays in gallery)"
          >
            <Trash2 size={12} /> Unlink
          </button>
        </div>
      </div>
    </div>
  );
}
